"use client";

import { useEffect, useState } from "react";

/**
 * Props for {@link LaunchingOverlay}.
 */
export interface LaunchingOverlayProps {
  /**
   * Title of the piece being prepared. Used both in the gradient headline
   * (`Virtuosa prépare {title}`) and the accessible label.
   */
  title: string;
}

const CASCADE_INTERVAL_MS = 600;
const CASCADE_STEPS = 5;

/**
 * Full-screen overlay rendered during the `launching` phase of the upload
 * modal. Takes over the viewport (z-50 + glass backdrop) so the user feels
 * Virtuosa is preparing their session — not running a generic loader.
 *
 * Visuals: violet halo with a rotating diamond, gradient headline, soft
 * subtitle, and a cascade of five diamonds that light up sequentially. The
 * cascade simulates progress and intentionally loops back to 0; the actual
 * end is governed by the modal's `handleAnalyze` (post-analysis close).
 */
export function LaunchingOverlay({ title }: LaunchingOverlayProps) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setStep((s) => (s + 1) % (CASCADE_STEPS + 1));
    }, CASCADE_INTERVAL_MS);

    return () => {
      window.clearInterval(id);
    };
  }, []);

  const displayedTitle = title.trim() === "" ? "ton morceau" : title;
  const accessibleLabel = `Préparation de ${displayedTitle}`;

  return (
    <div
      role="status"
      aria-label={accessibleLabel}
      aria-live="polite"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center upload-launch"
      style={{
        backgroundColor: "rgba(8, 7, 18, 0.80)",
        backdropFilter: "blur(16px)",
      }}
    >
      <div className="flex flex-col items-center gap-7">
        {/* Halo + diamond */}
        <div
          className="rounded-full flex items-center justify-center"
          style={{
            width: 42,
            height: 42,
            backgroundColor: "var(--color-violet-soft)",
            border: "1px solid rgba(167, 139, 250, 0.30)",
            boxShadow: "0 0 32px rgba(139, 92, 246, 0.20)",
          }}
        >
          <span
            aria-hidden
            className="inline-block"
            style={{
              width: 12,
              height: 12,
              transform: "rotate(45deg)",
              backgroundColor: "var(--color-accent-bright)",
              boxShadow: "0 0 12px rgba(167, 139, 250, 0.6)",
            }}
          />
        </div>

        {/* Headline + subtitle */}
        <div className="flex flex-col items-center gap-2 text-center px-6">
          <p
            style={{
              fontSize: 22,
              fontWeight: 500,
              background:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(255, 255, 255, 0.70) 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              color: "transparent",
              letterSpacing: "-0.01em",
            }}
          >
            Virtuosa prépare {displayedTitle}
          </p>
          <p
            className="text-[12px]"
            style={{ color: "var(--color-text-secondary)", opacity: 0.5 }}
          >
            Tu peux commencer à jouer dans quelques secondes
          </p>
        </div>

        {/* Cascade of five diamonds */}
        <div className="flex items-center gap-2" aria-hidden>
          {Array.from({ length: CASCADE_STEPS }, (_, i) => {
            const lit = i < step;

            return (
              <span
                key={i}
                className="inline-block"
                style={{
                  width: 6,
                  height: 6,
                  transform: "rotate(45deg)",
                  backgroundColor: lit
                    ? "var(--color-accent-bright)"
                    : "rgba(255, 255, 255, 0.10)",
                  boxShadow: lit ? "0 0 6px rgba(167, 139, 250, 0.5)" : "none",
                  transition: "background-color 200ms, box-shadow 200ms",
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
